<div class="record-payment-dialog">
  <header>
    <h2>Record Card Payment</h2>
    <div class="close" (click)="close()"></div>
  </header>

  <div *ngIf="loading" class="loading">Loading . . .</div>
  <div *ngIf="!loading && loadError" class="error">{{loadError}}</div>

  <ng-container *ngIf="!loading && !loadError && context">

    <div class="row">
      <label>Purchase</label>
      <div class="value">{{context.purchaseKey}} &middot; {{context.locationName}}</div>
    </div>
    <div class="row due">
      <label>Balance Due</label>
      <div class="value">{{money(context.due)}}</div>
    </div>

    <!-- Step 1 -->
    <section *ngIf="!confirming">
      <div class="row" *ngIf="context.ledgers.length > 1">
        <label>Ledger</label>
        <select [(ngModel)]="ledgerId" (ngModelChange)="ledgerChanged()">
          <option *ngFor="let ledger of context.ledgers" [ngValue]="ledger._id">{{ledger.name}}</option>
        </select>
      </div>

      <div class="mode-tabs">
        <button class="tab" [class.active]="mode === 'lookup'" [disabled]="!canLookup" (click)="setMode('lookup')">Look Up at Processor</button>
        <button class="tab" [class.active]="mode === 'manual'" (click)="setMode('manual')">Enter Manually</button>
      </div>

      <!-- lookup mode -->
      <ng-container *ngIf="mode === 'lookup'">
        <div class="hint">
          Enter the transaction ID from the processor's virtual terminal. The amount, card and
          authorization code are read from the processor; nothing is charged.
        </div>
        <div class="row input-row">
          <label>Transaction ID</label>
          <div class="inline">
            <input type="text" [(ngModel)]="processorTransactionId" placeholder="e.g. 906416816"
                   (keyup.enter)="doLookup()" />
            <button class="button" [disabled]="lookingUp || !processorTransactionId.trim()" (click)="doLookup()">
              {{lookingUp ? 'Looking Up . . .' : 'Look Up'}}
            </button>
          </div>
        </div>

        <div *ngIf="lookup && lookup.found" class="found" [class.unusable]="!lookupUsable">
          <div class="row"><label>Type / Result</label><div class="value">{{lookup.found.type}} &middot; {{lookup.found.result}}</div></div>
          <div class="row"><label>Amount</label><div class="value strong">{{money(lookup.amountCents)}}</div></div>
          <div class="row"><label>Card</label><div class="value">{{lookup.found.cardBrand || 'Card'}} <span *ngIf="lookup.found.lastFour">xxxx{{lookup.found.lastFour}}</span></div></div>
          <div class="row" *ngIf="lookup.found.authCode"><label>Auth Code</label><div class="value">{{lookup.found.authCode}}</div></div>
          <div class="row" *ngIf="lookup.found.chargedTime"><label>Charged</label><div class="value">{{lookup.found.chargedTime | rf:'dateTime'}}</div></div>
          <div class="row" *ngIf="lookup.found.batchId"><label>Batch</label><div class="value">{{lookup.found.batchId}}</div></div>
        </div>
        <div *ngIf="lookupError" class="error">{{lookupError}}</div>
      </ng-container>

      <!-- manual mode -->
      <ng-container *ngIf="mode === 'manual'">
        <div class="hint">
          Type the details from the processor's receipt. Nothing is charged. Without a processor
          transaction ID the payment cannot be refunded from the register.
        </div>
        <div class="row input-row">
          <label>Amount</label>
          <input class="amount" type="number" step="0.01" min="0.01" [(ngModel)]="amountInput" />
        </div>
        <div *ngIf="manualAmountError" class="error small">{{manualAmountError}}</div>
        <div class="row input-row">
          <label>Card Brand</label>
          <select [(ngModel)]="cardBrand">
            <option value="">—</option>
            <option *ngFor="let brand of context.cardBrands" [value]="brand">{{brand}}</option>
          </select>
        </div>
        <div class="row input-row">
          <label>Last Four</label>
          <input type="text" inputmode="numeric" maxlength="4" [(ngModel)]="lastFour" placeholder="1004" />
        </div>
        <div class="row input-row">
          <label>Auth Code</label>
          <input type="text" [(ngModel)]="authCode" placeholder="209423" />
        </div>
        <div class="row input-row">
          <label>Processor Transaction ID</label>
          <input type="text" [(ngModel)]="manualReference" placeholder="optional" />
        </div>
      </ng-container>

      <div class="note-row">
        <label>Note</label>
        <textarea [(ngModel)]="note" placeholder="Where and why this payment was taken"></textarea>
      </div>

      <div class="buttons">
        <button class="button grey" (click)="close()">Cancel</button>
        <button class="button green" [disabled]="!canContinue()" (click)="continueToConfirm()">Continue</button>
      </div>
    </section>

    <!-- Step 2: confirmation -->
    <section *ngIf="confirming" class="confirm">
      <div class="confirm-title">Confirm Payment Record</div>
      <div class="row"><label>Amount</label><div class="value strong">{{money(summaryAmountCents)}}</div></div>
      <div class="row" *ngIf="summaryCard"><label>Card</label><div class="value">{{summaryCard}}</div></div>
      <div class="row" *ngIf="summaryAuth"><label>Auth Code</label><div class="value">{{summaryAuth}}</div></div>
      <div class="row" *ngIf="summaryReference"><label>Transaction ID</label><div class="value">{{summaryReference}}</div></div>
      <div class="row"><label>Remaining Due</label><div class="value">{{money(remainingAfter)}}</div></div>
      <div class="note-block" *ngIf="note.trim()">
        <label>Note</label>
        <div class="note-value">{{note.trim()}}</div>
      </div>
      <div class="confirm-text">
        This records the card payment on the purchase without charging the card.
        <ng-container *ngIf="remainingAfter === 0">The purchase will be completed now and reported on today's sales.</ng-container>
        <ng-container *ngIf="remainingAfter > 0">The purchase stays open with {{money(remainingAfter)}} due.</ng-container>
      </div>
      <div class="buttons">
        <button class="button grey" [disabled]="saving" (click)="back()">Back</button>
        <button class="button green" [disabled]="saving" (click)="submit()">{{saving ? 'Recording . . .' : 'Record Payment'}}</button>
      </div>
    </section>

    <div *ngIf="errorMessage" class="error">{{errorMessage}}</div>
  </ng-container>
</div>
